import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Globe, MapPin, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { footerData } from "../data/footerData";
import ContactModal from "../components/common/ContactModal";
import { assetPath } from "../utils/assetPath";

/* ─── Shared animation presets ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const linkClass =
  "text-[#B5A598] hover:text-white text-[13px] font-medium transition-colors duration-500";
const subLinkClass =
  "text-[#B5A598] hover:text-white/65 text-[12.5px] font-medium transition-colors duration-500 pl-3";
const headingClass =
  "text-[11px] font-bold  tracking-[0.15em] uppercase text-white mb-5";

const Footer = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [activeMapTab, setActiveMapTab] = useState("india");
  const footer = footerData;

  return (
    <footer
      role="contentinfo"
      className="w-full relative m-0 p-0 block shrink-0"
      style={{
        background: "linear-gradient(180deg, #2a2a2a 0%, #181818 100%)",
      }}
    >
      <motion.div
        className="max-w-[1400px] mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { staggerChildren: 0.12 },
          },
        }}
      >
        {/* ═══════════════════════════════════════════
            SECTION 1 — "Stay In Touch" Hero
        ═══════════════════════════════════════════ */}
        <motion.div
          className="px-8 md:px-16 lg:px-20 pt-16 md:pt-20 pb-10"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 1, ease: [0.22, 1, 0.36, 1] },
            },
          }}
        >
          <h2 className="text-4xl md:text-6xl lg:text-[5.5rem] font-semibold tracking-tight leading-[1.1] text-[#E0CCBB]">
            Stay In Touch
          </h2>

          <div className="mt-12 md:mt-16">
            <div className="flex justify-end mb-4">
              <Link
                to="/"
                className="flex items-center gap-2 text-sm font-semibold tracking-wide text-white/80 hover:text-white transition-colors duration-500"
              >
                <span className="text-white">✦</span> Explore Home
              </Link>
            </div>
            <div className="w-full h-px bg-white/10" />
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════
            SECTION 2 — Navigation Grid
            Brand | Platform | Industries | Contact
        ═══════════════════════════════════════════ */}
        <div className="px-8 md:px-16 lg:px-20 pb-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_1fr_1fr_1.2fr] gap-y-10 gap-x-8 items-start">
          {/* ── Brand Column ── */}
          <motion.div variants={fadeUp} className="flex flex-col gap-5">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={assetPath(footer.brand.logo)}
                alt={footer.brand.name}
                className="h-12 w-auto opacity-90 group-hover:opacity-100 transition-opacity duration-500"
              />
            </Link>
            <p className="text-white/40 text-[13px] leading-relaxed font-medium max-w-[220px]">
              {footer.brand.description}
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-3 mt-1">
              {footer.socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 hover:bg-white/10"
                  style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <img
                    src={assetPath(`/Footer/${link.name}.svg`)}
                    alt={link.name}
                    className="w-4 h-4 opacity-60 hover:opacity-100 transition-opacity"
                  />
                </a>
              ))}
            </div>
          </motion.div>

          {/* ── Platform Column ── */}
          <motion.div variants={fadeUp}>
            <h4 className={headingClass}> ✦ Platform</h4>
            <div className="flex flex-col gap-2">
              <Link to="/product/raptor" className={linkClass}>
                RAPTOR
              </Link>
              <Link to="/product/learnon" className={linkClass}>
                LearnOn
              </Link>
              <Link to="/product/aurora" className={linkClass}>
                Aurora
              </Link>
              <Link to="/product/griffin" className={linkClass}>
                GRIFFIN XR
              </Link>
              <Link to="/product/courseai" className={linkClass}>
                CourseAI
              </Link>
            </div>
          </motion.div>

          {/* ── Industries Column ── */}
          <motion.div variants={fadeUp}>
            <h4 className={headingClass}> ✦ Industries</h4>
            <div className="flex flex-col gap-2">
              <Link
                to="/industries/aviation"
                className="text-white/60 hover:text-white text-[13px] font-semibold transition-colors duration-500"
              >
                Aviation
              </Link>
              <Link
                to="/industries/aviation/flight-deck"
                className={subLinkClass}
              >
                Flight Deck
              </Link>
              <Link
                to="/industries/aviation/engineers"
                className={subLinkClass}
              >
                Engineers
              </Link>
              <Link
                to="/industries/aviation/cabin-crew"
                className={subLinkClass}
              >
                Cabin Crew
              </Link>

              <div className="w-5 h-px bg-white/8 my-1" />

              <Link
                to="/industries/defence"
                className="text-white/60 hover:text-white text-[13px] font-semibold transition-colors duration-500"
              >
                Defence
              </Link>
              <Link to="/industries/defence/air-force" className={subLinkClass}>
                Air Force
              </Link>
              <Link to="/industries/defence/navy" className={subLinkClass}>
                Navy
              </Link>
              <Link to="/industries/defence/army" className={subLinkClass}>
                Army
              </Link>
              <Link
                to="/industries/defence/special-operations"
                className={subLinkClass}
              >
                Special Ops
              </Link>

              <div className="w-5 h-px bg-white/8 my-1" />

              <Link
                to="/industries/energy"
                className="text-white/60 hover:text-white text-[13px] font-semibold transition-colors duration-500"
              >
                Energy
              </Link>
            </div>
          </motion.div>

          {/* ── Company / Quick Links Column ── */}
          <motion.div variants={fadeUp}>
            <h4 className={headingClass}>✦ Company</h4>
            <div className="flex flex-col gap-2">
              <Link to="/about" className={linkClass}>
                About Us
              </Link>
              <Link to="/about/the-idea" className={linkClass}>
                The Idea
              </Link>
              <Link to="/about/quest" className={linkClass}>
                Quest
              </Link>

              <div className="w-5 h-px bg-white/8 my-1" />

              <button
                onClick={() => setIsContactModalOpen(true)}
                className="text-left text-[#B5A598] hover:text-white text-[13px] font-medium transition-colors duration-500 cursor-pointer"
              >
                Request Demo
              </button>
            </div>
          </motion.div>

          {/* ── Contact Column ── */}
          <motion.div variants={fadeUp}>
            <h4 className={headingClass}>✦ Contact</h4>
            <div className="flex flex-col gap-3.5">
              <a
                href={`https://wa.me/${footer.contact.whatsapp}?text=${encodeURIComponent(footer.contact.whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-[#B5A598] hover:text-white text-[13px] font-medium transition-colors duration-500"
              >
                <Phone size={14} className="shrink-0 text-[#B5A598]" />
                {footer.contact.phone}
              </a>

              <a
                href={`mailto:${footer.contact.email}`}
                className="flex items-center gap-2.5 text-[#B5A598] hover:text-white text-[13px] font-medium transition-colors duration-500"
              >
                <Mail size={14} className="shrink-0 text-[#B5A598]" />
                {footer.contact.email}
              </a>

              {/* Addresses */}
              {footer.offices.map((office) => (
                <a
                  key={office.id}
                  href={office.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2.5 text-white/55 hover:text-white text-[13px] font-medium transition-colors duration-500 group"
                >
                  <MapPin
                    size={14}
                    className="shrink-0 mt-0.5 text-white/40 group-hover:text-white/70 transition-colors"
                  />
                  <span className="flex flex-col gap-px">
                    <span className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#B5A598] flex items-center gap-1">
                      {office.flag} {office.location}
                    </span>
                    <span className="leading-relaxed text-[12.5px] text-[#B5A598]">
                      {office.address}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ═══════════════════════════════════════════
            SECTION 3 — Map Embed (full width)
        ═══════════════════════════════════════════ */}
        <motion.div variants={fadeUp} className="px-8 md:px-16 lg:px-20 pb-10">
          {/* Tab Switcher */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-white/30 mr-2">
              Office
            </span>
            {footer.offices.map((office) => (
              <button
                key={office.id}
                onClick={() => setActiveMapTab(office.id)}
                className="px-3.5 py-1.5 rounded-full text-[11px] font-semibold tracking-wide transition-all duration-400 cursor-pointer"
                style={{
                  background:
                    activeMapTab === office.id
                      ? "rgba(255,255,255,0.1)"
                      : "transparent",
                  color:
                    activeMapTab === office.id
                      ? "rgba(255,255,255,0.9)"
                      : "rgba(255,255,255,0.35)",
                  border:
                    activeMapTab === office.id
                      ? "1px solid rgba(255,255,255,0.12)"
                      : "1px solid rgba(255,255,255,0.06)",
                }}
              >
                {office.flag} {office.id === "india" ? "India" : "USA"}
              </button>
            ))}
          </div>

          {/* Map */}
          <div className="w-full h-[200px] rounded-2xl overflow-hidden  invert opacity-40 hover:grayscale-0 hover:invert-0 hover:opacity-100 transition-all duration-700 border border-white/5">
            <iframe
              title={`Office Location — ${activeMapTab}`}
              src={
                footer.offices.find((o) => o.id === activeMapTab)?.embedUrl ||
                footer.offices[0].embedUrl
              }
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════
            SECTION 4 — Bottom Bar
        ═══════════════════════════════════════════ */}
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                duration: 1,
                delay: 0.5,
                ease: [0.22, 1, 0.36, 1],
              },
            },
          }}
          className="px-8 md:px-16 lg:px-20 py-6 flex flex-col md:flex-row items-center justify-between gap-4"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <p className="text-white/50 text-[12px] font-medium tracking-wide m-0">
            {footer.copyright}
          </p>

          <div className="flex items-center gap-6">
            {footer.legalLinks.map((link, index) => (
              <Link
                key={index}
                to={link.url}
                className="text-white/50 hover:text-white text-[11px] font-semibold uppercase tracking-widest transition-colors duration-500"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </motion.div>
      </motion.div>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </footer>
  );
};

export default Footer;
